/*
 * cx-photo-compose.js -- flattens a cx-photo design into ONE canvas.
 * Used for both the on-page preview and the print file, so what the
 * customer sees is exactly what is printed (only sharper).
 *
 *   CXPhotoCompose.polaroid({ photo, frame, caption, font, height })
 *     photo   : square canvas, already processed (effect applied)
 *     frame   : 'White' | 'Black'
 *     caption : string (<= 25 chars, may be empty)
 *     font    : 'handwritten' | 'script' | 'classic'
 *     height  : output height in px (biggest dimension = height)
 *   CXPhotoCompose.polaroidLayout(height) -> geometry (photo size etc.)
 *   CXPhotoCompose.sketchPatch({ art, height }) -> rounded paper patch
 *   CXPhotoCompose.FONTS -> font table
 */
(function (global) {
  'use strict';

  var FONTS = {
    handwritten: { label: 'Handwritten', family: "'cxp-kalam', 'cxp-kalam-deva', cursive", weight: '700', scale: 1.0, lift: 0.03 },
    script: { label: 'Script', family: "'cxp-dancing', 'cxp-kalam-deva', cursive", weight: '700', scale: 1.14, lift: 0.0 },
    classic: { label: 'Classic', family: "'cxp-playfair', 'cxp-kalam-deva', serif", weight: 'italic 600', scale: 0.96, lift: 0.02 },
  };

  // Polaroid proportions: width = 0.83 x height, even side/top margins, a
  // deeper bottom band that carries the caption (about 22% of the height).
  var ASPECT = 0.83;
  var MARGIN = 0.0498; // of height

  function polaroidLayout(height) {
    var H = Math.round(height);
    var W = Math.round(H * ASPECT);
    var m = Math.round(H * MARGIN);
    var photo = W - 2 * m;
    var bandTop = m + photo;
    return { W: W, H: H, m: m, photo: photo, bandTop: bandTop, band: H - bandTop };
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function fontString(f, px) {
    return f.weight + ' ' + Math.round(px) + 'px ' + f.family;
  }

  // Best 2-line split at a word boundary (minimises the longer line).
  function bestTwoLineSplit(ctx, words) {
    var best = null;
    for (var i = 1; i < words.length; i++) {
      var a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
      var wmax = Math.max(ctx.measureText(a).width, ctx.measureText(b).width);
      if (!best || wmax < best.w) best = { lines: [a, b], w: wmax };
    }
    return best;
  }

  // Fit the caption into the band: one big line if it fits comfortably,
  // otherwise two balanced lines. Never squashes text horizontally.
  function layoutCaption(ctx, text, f, maxW, band) {
    var probe = 100;
    ctx.font = fontString(f, probe);
    var oneW = ctx.measureText(text).width || 1;
    var oneSize = Math.min(band * 0.44 * f.scale, (probe * maxW) / oneW);
    var result = { lines: [text], size: oneSize };
    var words = text.split(/\s+/).filter(Boolean);
    if (words.length > 1 && oneSize < band * 0.34 * f.scale) {
      var split = bestTwoLineSplit(ctx, words);
      if (split) {
        var twoSize = Math.min(band * 0.355 * f.scale, (probe * maxW) / split.w);
        if (twoSize > oneSize * 1.08) result = { lines: split.lines, size: twoSize };
      }
    }
    return result;
  }

  function polaroid(opts) {
    var L = polaroidLayout(opts.height);
    var c = document.createElement('canvas');
    c.width = L.W;
    c.height = L.H;
    var ctx = c.getContext('2d');
    var isBlack = opts.frame === 'Black';
    var radius = Math.round(L.W * 0.022);

    // frame card
    roundRectPath(ctx, 0, 0, L.W, L.H, radius);
    ctx.fillStyle = isBlack ? '#151515' : '#FBF9F4';
    ctx.fill();
    ctx.save();
    roundRectPath(ctx, 0, 0, L.W, L.H, radius);
    ctx.clip();

    // photo
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(opts.photo, L.m, L.m, L.photo, L.photo);
    // a hairline at the photo edge, like the slight step of a real print
    ctx.lineWidth = Math.max(1, L.W * 0.0022);
    ctx.strokeStyle = isBlack ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.13)';
    ctx.strokeRect(L.m + ctx.lineWidth / 2, L.m + ctx.lineWidth / 2, L.photo - ctx.lineWidth, L.photo - ctx.lineWidth);

    // caption
    var text = (opts.caption || '').trim();
    if (text) {
      var f = FONTS[opts.font] || FONTS.handwritten;
      var maxW = L.W - L.m * 2 * 1.35;
      var lay = layoutCaption(ctx, text, f, maxW, L.band);
      ctx.font = fontString(f, lay.size);
      ctx.fillStyle = isBlack ? '#F4F0E8' : '#2A2521';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      var lineH = lay.size * 1.08;
      var centerY = L.bandTop + L.band * (0.48 - f.lift - (lay.lines.length > 1 ? 0.025 : 0));
      var startY = centerY - ((lay.lines.length - 1) * lineH) / 2;
      for (var i = 0; i < lay.lines.length; i++) {
        ctx.fillText(lay.lines[i], L.W / 2, startY + i * lineH);
      }
    }
    ctx.restore();
    return c;
  }

  // Charcoal sketch: the drawing (already on cream paper) clipped to a
  // rounded rectangle, 4:5 portrait, with a faint edge so the sheet reads
  // as paper on any surface.
  function sketchPatch(opts) {
    var H = Math.round(opts.height);
    var W = Math.round(H * 0.8);
    var c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    var ctx = c.getContext('2d');
    var r = Math.round(W * 0.045);
    ctx.save();
    roundRectPath(ctx, 0, 0, W, H, r);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(opts.art, 0, 0, W, H);
    // slightly darker deckle toward the sheet edge
    var g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.42, W / 2, H / 2, Math.max(W, H) * 0.72);
    g.addColorStop(0, 'rgba(120,96,60,0)');
    g.addColorStop(1, 'rgba(120,96,60,0.16)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
    ctx.lineWidth = Math.max(1, W * 0.003);
    ctx.strokeStyle = 'rgba(90,70,40,0.25)';
    roundRectPath(ctx, ctx.lineWidth / 2, ctx.lineWidth / 2, W - ctx.lineWidth, H - ctx.lineWidth, r);
    ctx.stroke();
    return c;
  }

  global.CXPhotoCompose = {
    FONTS: FONTS,
    polaroid: polaroid,
    polaroidLayout: polaroidLayout,
    sketchPatch: sketchPatch,
    SKETCH_ASPECT: 0.8,
  };
})(window);
